import type { ReactNode } from 'react';
import type { SelectFilter, SelectOption } from './types.js';

export function optionLabelText(label: ReactNode): string {
  if (typeof label === 'string') return label;
  if (typeof label === 'number') return String(label);
  return '';
}

/** Text used for searching + typeahead. Prefers explicit `searchText`
 *  (needed when `label` is a ReactNode) otherwise derives from string label. */
export function optionSearchText<V extends string>(opt: SelectOption<V>): string {
  if (opt.searchText !== undefined) return opt.searchText;
  return optionLabelText(opt.label);
}

export function ensurePortalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let root = document.getElementById('vq-portal-root');
  if (root === null) {
    root = document.createElement('div');
    root.id = 'vq-portal-root';
    root.className = 'vq-portal-root';
    document.body.appendChild(root);
  }
  return root;
}

export function findEnabledIndex<V extends string>(
  options: readonly SelectOption<V>[],
  start: number,
  direction: 1 | -1,
  predicate?: (i: number) => boolean,
): number {
  const len = options.length;
  if (len === 0) return -1;
  let i = start;
  for (let step = 0; step < len; step += 1) {
    i = (i + direction + len) % len;
    const opt = options[i];
    if (opt === undefined || opt.disabled === true) continue;
    if (predicate !== undefined && !predicate(i)) continue;
    return i;
  }
  return -1;
}

/** `contains` = case-insensitive substring, also matches CJK directly.
 *  `fuzzy` = matches query chars in order, non-contiguous. */
export function matchQuery(text: string, query: string, mode: SelectFilter): boolean {
  if (query.length === 0) return true;
  if (mode === 'none') return true;
  const t = text.toLocaleLowerCase();
  const q = query.toLocaleLowerCase();
  if (mode === 'contains') return t.includes(q);
  // fuzzy
  let ti = 0;
  for (const ch of q) {
    const idx = t.indexOf(ch, ti);
    if (idx === -1) return false;
    ti = idx + 1;
  }
  return true;
}

/** Compute which options are visible under the current query + groups.
 *  Returns indices into the original `options` array so highlight math
 *  keeps referencing stable positions. Groups with zero visible items are
 *  elided. */
export function computeVisibleIndices<V extends string>(
  options: readonly SelectOption<V>[],
  query: string,
  mode: SelectFilter,
  filterFn?: (option: SelectOption<V>, query: string) => boolean,
): number[] {
  const visible: number[] = [];
  for (let i = 0; i < options.length; i += 1) {
    const opt = options[i];
    if (opt === undefined) continue;
    const matched =
      filterFn !== undefined
        ? filterFn(opt, query)
        : matchQuery(optionSearchText(opt), query, mode);
    if (matched) visible.push(i);
  }
  return visible;
}
